import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
    Avatar,
    Badge,
    Button,
    Card,
    Col,
    Divider,
    Empty,
    Grid,
    Layout,
    Row,
    Space,
    Tag,
    Typography,
} from 'antd';
import {
    ApiOutlined,
    ArrowLeftOutlined,
    AudioOutlined,
    BookOutlined,
    ClockCircleOutlined,
    FileTextOutlined,
    IdcardOutlined,
    MessageOutlined,
    PictureOutlined,
    RobotOutlined,
    TeamOutlined,
    UserOutlined,
    VideoCameraOutlined,
} from '@ant-design/icons';
import { getAgentById } from '../AgentServices';
import { useGlobal } from '../../Shared/GlobalContext/GlobalContext';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const label = (t, key, defaultValue) => t(key, { defaultValue });

const formatDate = (value) => {
    if (!value) return '-';
    try {
        return format(new Date(value), 'yyyy-MM-dd HH:mm');
    } catch (error) {
        return value;
    }
};

const formatDuration = (t, value) => {
    if (value === null || value === undefined || value === '') return '-';
    return `${value} ${label(t, 'seconds', 'seconds')}`;
};

const getPlatformLabel = (t, channelType) => {
    if (!channelType) return label(t, 'channel', 'Channel');
    return t(channelType, { defaultValue: channelType.replace(/_/g, ' ') });
};

const BooleanTag = ({ value, t }) => (
    <Tag color={value ? 'success' : 'default'} style={{ margin: 0 }}>
        {value ? t('enabled') : t('disabled')}
    </Tag>
);

const InfoItem = ({ label: itemLabel, value, copyable = false }) => (
    <div style={{ minWidth: 0 }}>
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
            {itemLabel}
        </Text>
        {React.isValidElement(value) ? (
            value
        ) : copyable && value && value !== '-' ? (
            <Paragraph copyable={{ text: String(value), tooltips: false }} style={{ marginBottom: 0 }}>
                <Text strong style={{ wordBreak: 'break-word' }}>{value}</Text>
            </Paragraph>
        ) : (
            <Text strong style={{ wordBreak: 'break-word' }}>{value || '-'}</Text>
        )}
    </div>
);

const SectionCard = ({ title, icon, children }) => (
    <Card
        title={
            <Space size={8}>
                {icon}
                <span>{title}</span>
            </Space>
        }
        bordered={false}
        style={{ height: '100%' }}
    >
        {children}
    </Card>
);

const AgentDetail = () => {
    const { t } = useTranslation();
    const { agentId } = useParams();
    const navigate = useNavigate();
    const screens = useBreakpoint();
    const { setLoading } = useGlobal();
    const { handleError } = useErrorHandler();
    const { isDarkMode } = useTheme();
    const [agent, setAgent] = useState(null);

    const isMobile = !screens.md;

    const fetchAgent = useCallback(async () => {
        if (!agentId) {
            return;
        }

        try {
            setLoading(true);
            const response = await getAgentById(agentId);
            setAgent(response.data);
        } catch (error) {
            handleError(error);
            navigate('/agents');
        } finally {
            setLoading(false);
        }
    }, [agentId, handleError, navigate, setLoading]);

    useEffect(() => {
        fetchAgent();
    }, [fetchAgent]);

    if (!agentId) {
        return (
            <Content>
                <Card>
                    <Text type="danger">{label(t, 'no_agent_id_provided', 'No agent ID provided')}</Text>
                </Card>
            </Content>
        );
    }

    if (!agent) {
        return null;
    }

    const capabilities = [
        { key: 'process_audio', icon: <AudioOutlined />, label: label(t, 'filters_field_process_audio', 'Process Audio'), value: agent.process_audio },
        { key: 'process_image', icon: <PictureOutlined />, label: label(t, 'filters_field_process_image', 'Process Image'), value: agent.process_image },
        { key: 'process_video', icon: <VideoCameraOutlined />, label: label(t, 'filters_field_process_video', 'Process Video'), value: agent.process_video },
        { key: 'process_document', icon: <FileTextOutlined />, label: label(t, 'filters_field_process_document', 'Process Document'), value: agent.process_document },
        { key: 'allow_ticket_creation', icon: <MessageOutlined />, label: label(t, 'filters_field_allow_ticket_creation', 'Allow Ticket Creation'), value: agent.allow_ticket_creation },
    ];

    const channels = agent.channels || [];
    const knowledgeBases = agent.knowledge_bases || [];
    const admins = agent.admin_contacts || [];

    return (
        <Content>
            <div style={{ marginBottom: 24 }}>
                <Space size={8} wrap>
                    <Link to="/agents">
                        <Button icon={<ArrowLeftOutlined />} type="text" />
                    </Link>
                    <Text type="secondary">{t('agents')}</Text>
                    <Text type="secondary">/</Text>
                    <Text strong>{agent.name}</Text>
                </Space>
            </div>

            <Card
                bordered={false}
                style={{
                    marginBottom: 24,
                    boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.05)',
                }}
                bodyStyle={{ padding: isMobile ? 16 : 24 }}
            >
                <Space align="start" size={16} style={{ width: '100%' }}>
                    <Avatar
                        size={isMobile ? 56 : 72}
                        shape="square"
                        icon={<RobotOutlined />}
                        style={{
                            borderRadius: 12,
                            background: isDarkMode ? '#1f1f1f' : '#f0f5ff',
                            color: isDarkMode ? '#8c8c8c' : '#1677ff',
                        }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <Space wrap size={8} style={{ marginBottom: 6 }}>
                            <Title level={2} style={{ margin: 0, fontSize: isMobile ? 22 : 28, wordBreak: 'break-word' }}>
                                {agent.name}
                            </Title>
                            <Badge status={agent.is_started ? 'success' : 'default'} text={agent.is_started ? t('started') : t('stopped')} />
                        </Space>
                        <Row gutter={[16, 12]}>
                            <Col xs={24} md={8}>
                                <InfoItem label={label(t, 'agent_id', 'Agent ID')} value={agent.id} copyable />
                            </Col>
                            <Col xs={24} md={8}>
                                <InfoItem label={label(t, 'user_id', 'User ID')} value={agent.user_id} copyable />
                            </Col>
                            <Col xs={24} md={8}>
                                <InfoItem label={t('updated_at')} value={formatDate(agent.updated_at)} />
                            </Col>
                        </Row>
                    </div>
                </Space>
            </Card>

            <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                    <SectionCard title={t('basic_information')} icon={<IdcardOutlined />}>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12}>
                                <InfoItem label={t('created_at')} value={formatDate(agent.created_at)} />
                            </Col>
                            <Col xs={24} sm={12}>
                                <InfoItem label={t('updated_at')} value={formatDate(agent.updated_at)} />
                            </Col>
                            <Col xs={24} sm={12}>
                                <InfoItem label={label(t, 'channels', 'Channels')} value={channels.length} />
                            </Col>
                            <Col xs={24} sm={12}>
                                <InfoItem label={label(t, 'knowledge_bases', 'Knowledge Bases')} value={knowledgeBases.length} />
                            </Col>
                        </Row>
                    </SectionCard>
                </Col>

                <Col xs={24} lg={12}>
                    <SectionCard title={label(t, 'capabilities', 'Capabilities')} icon={<ApiOutlined />}>
                        <Row gutter={[12, 12]}>
                            {capabilities.map((item) => (
                                <Col xs={24} sm={12} key={item.key}>
                                    <Space size={8}>
                                        {item.icon}
                                        <Text>{item.label}</Text>
                                        <BooleanTag value={item.value} t={t} />
                                    </Space>
                                </Col>
                            ))}
                        </Row>
                    </SectionCard>
                </Col>

                <Col xs={24} lg={12}>
                    <SectionCard title={label(t, 'processing_settings', 'Processing Settings')} icon={<ClockCircleOutlined />}>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12}>
                                <InfoItem label={label(t, 'filters_field_processing_delay_enabled', 'Processing Delay')} value={<BooleanTag value={agent.is_processing_delay_enabled} t={t} />} />
                            </Col>
                            <Col xs={24} sm={12}>
                                <InfoItem label={label(t, 'processing_delay', 'Processing Delay')} value={formatDuration(t, agent.processing_delay)} />
                            </Col>
                            <Col xs={24} sm={12}>
                                <InfoItem label={label(t, 'processing_jitter', 'Processing Jitter')} value={formatDuration(t, agent.processing_jitter)} />
                            </Col>
                        </Row>
                    </SectionCard>
                </Col>

                <Col xs={24} lg={12}>
                    <SectionCard title={label(t, 'consumption_settings', 'Consumption Settings')} icon={<ClockCircleOutlined />}>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12}>
                                <InfoItem label={label(t, 'filters_field_consumption_limit', 'Consumption Limit')} value={<BooleanTag value={agent.is_consumption_limit_enabled} t={t} />} />
                            </Col>
                            <Col xs={24} sm={12}>
                                <InfoItem label={label(t, 'max_coins_consumption', 'Max Coins Consumption')} value={agent.max_coins_consumption ?? '-'} />
                            </Col>
                            <Col xs={24} sm={12}>
                                <InfoItem label={label(t, 'consumption_duration', 'Consumption Duration')} value={formatDuration(t, agent.consumption_duration)} />
                            </Col>
                            <Col xs={24} sm={12}>
                                <InfoItem label={label(t, 'snooze_duration', 'Snooze Duration')} value={formatDuration(t, agent.snooze_duration)} />
                            </Col>
                        </Row>
                    </SectionCard>
                </Col>

                <Col xs={24}>
                    <SectionCard title={t('agent_instructions')} icon={<FileTextOutlined />}>
                        <Paragraph
                            dir="auto"
                            style={{
                                marginBottom: 0,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                unicodeBidi: 'plaintext',
                            }}
                        >
                            {agent.system_instruction || '-'}
                        </Paragraph>
                    </SectionCard>
                </Col>

                <Col xs={24} xl={12}>
                    <SectionCard title={t('channels')} icon={<ApiOutlined />}>
                        {channels.length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('no_channels')} />
                        ) : (
                            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                {channels.map((channel) => (
                                    <div key={channel.id}>
                                        <Space align="start" style={{ width: '100%' }}>
                                            <Avatar shape="square" icon={<ApiOutlined />} src={channel.picture_url} />
                                            <div style={{ minWidth: 0, flex: 1 }}>
                                                <Space wrap size={6}>
                                                    <Text strong>{channel.name || channel.external_channel_id || '-'}</Text>
                                                    <Tag color={channel.is_virtual ? 'purple' : 'blue'} style={{ margin: 0 }}>
                                                        {channel.is_virtual ? label(t, 'virtual', 'Virtual') : getPlatformLabel(t, channel.channel_type)}
                                                    </Tag>
                                                    <BooleanTag value={channel.is_synchronization_enabled} t={t} />
                                                </Space>
                                                <Row gutter={[12, 8]} style={{ marginTop: 8 }}>
                                                    <Col xs={24} sm={12}>
                                                        <InfoItem label={t('channel_id')} value={channel.id} copyable />
                                                    </Col>
                                                    <Col xs={24} sm={12}>
                                                        <InfoItem label={t('external_channel_id')} value={channel.external_channel_id} copyable />
                                                    </Col>
                                                    <Col xs={24} sm={12}>
                                                        <InfoItem label={t('created_at')} value={formatDate(channel.created_at)} />
                                                    </Col>
                                                    <Col xs={24} sm={12}>
                                                        <InfoItem label={t('updated_at')} value={formatDate(channel.updated_at)} />
                                                    </Col>
                                                </Row>
                                            </div>
                                        </Space>
                                        <Divider style={{ margin: '12px 0 0' }} />
                                    </div>
                                ))}
                            </Space>
                        )}
                    </SectionCard>
                </Col>

                <Col xs={24} xl={12}>
                    <SectionCard title={t('knowledge_bases')} icon={<BookOutlined />}>
                        {knowledgeBases.length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('no_knowledge_bases')} />
                        ) : (
                            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                {knowledgeBases.map((kb) => (
                                    <div key={kb.id}>
                                        <Space align="start" style={{ width: '100%' }}>
                                            <Avatar shape="square" icon={<BookOutlined />} />
                                            <div style={{ minWidth: 0, flex: 1 }}>
                                                <Space wrap size={6}>
                                                    <Text strong>{kb.name}</Text>
                                                    <BooleanTag value={!kb.is_disabled} t={t} />
                                                </Space>
                                                {kb.description && (
                                                    <Paragraph style={{ margin: '6px 0 0' }}>{kb.description}</Paragraph>
                                                )}
                                                <Row gutter={[12, 8]} style={{ marginTop: 8 }}>
                                                    <Col xs={24} sm={12}>
                                                        <InfoItem label={label(t, 'knowledge_base_id', 'Knowledge Base ID')} value={kb.id} copyable />
                                                    </Col>
                                                    <Col xs={24} sm={12}>
                                                        <InfoItem label={label(t, 'entry_count', 'Entry Count')} value={kb.entry_count ?? 0} />
                                                    </Col>
                                                    <Col xs={24} sm={12}>
                                                        <InfoItem label={label(t, 'qa_count', 'Q&A Count')} value={kb.qa_count ?? 0} />
                                                    </Col>
                                                    <Col xs={24} sm={12}>
                                                        <InfoItem label={label(t, 'business_info_count', 'Business Info Count')} value={kb.business_info_count ?? 0} />
                                                    </Col>
                                                </Row>
                                            </div>
                                        </Space>
                                        <Divider style={{ margin: '12px 0 0' }} />
                                    </div>
                                ))}
                            </Space>
                        )}
                    </SectionCard>
                </Col>

                <Col xs={24}>
                    <SectionCard title={t('admins')} icon={<TeamOutlined />}>
                        {admins.length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('no_admins')} />
                        ) : (
                            <Row gutter={[16, 16]}>
                                {admins.map((admin) => (
                                    <Col xs={24} md={12} xl={8} key={admin.id}>
                                        <Space align="start">
                                            <Avatar src={admin.picture_url} icon={<UserOutlined />} />
                                            <div style={{ minWidth: 0 }}>
                                                <Text strong style={{ display: 'block' }}>{admin.full_name || admin.email || t('unknown')}</Text>
                                                <Text type="secondary" style={{ display: 'block', wordBreak: 'break-word' }}>{admin.id}</Text>
                                                {admin.email && <Text type="secondary" style={{ display: 'block' }}>{admin.email}</Text>}
                                                {admin.phone && <Text type="secondary" style={{ display: 'block' }}>{admin.phone}</Text>}
                                            </div>
                                        </Space>
                                    </Col>
                                ))}
                            </Row>
                        )}
                    </SectionCard>
                </Col>
            </Row>
        </Content>
    );
};

export default AgentDetail;
